import { useState, useEffect, useCallback } from "react";

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    // During SSR or initial mount, avoid accessing window if possible to prevent hydration mismatch
    // However, for client-only app like this, window check is fine.
    // The error #321 usually means hooks are called outside of component.
    // But just in case, let's be safer with the initial state function.
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  const handleChange = useCallback(() => {
    if (typeof window !== "undefined") {
      setMatches(window.matchMedia(query).matches);
    }
  }, [query]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const matchMedia = window.matchMedia(query);

    // Trigger an initial check to ensure state sync
    handleChange();

    // Listen for changes
    // Safari < 14 uses addListener/removeListener, newer browsers use addEventListener/removeEventListener
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener("change", handleChange);
      return () => matchMedia.removeEventListener("change", handleChange);
    } else {
      // Deprecated method for older Safari versions
      matchMedia.addListener(handleChange);
      return () => matchMedia.removeListener(handleChange);
    }
  }, [query, handleChange]);

  return matches;
};

export default useMediaQuery;
